import React from 'react';
import { Control, FieldErrors, FieldArrayWithId, UseFormClearErrors, UseFormRegister, UseFormSetError, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { RoomSelected, RoomStatus } from './CreateBookingComponentV4'; // Sesuaikan path jika perlu
import DateInput from '../../../../shared/components/form/DateInput';

// Asumsi komponen anak sudah ada versi V4-nya
import RoomRatesComponentV4 from './RoomRatesComponentV4';
import { CreateBookingFormV4 } from '../../../../services/create-booking-v4/schema/CreateBookingSchemaV4';
import { CreatedRoom } from '../../../../services/create-booking-v4/hooks/useCreateGroupBookingV4';
import { ShowBookingDataV4 } from '../../../../services/create-booking-v4/schema/ShowBookingResponseV4';
import OtaOrLogo from '../../../pms-calendar/components/calendar/OtaOrLogo';
import { NAVIGATION_URLS } from '../../../../shared/utils/urls';

interface InvoiceFormPropsV4 {
    // fields berasal dari useFieldArray
    fields: FieldArrayWithId<CreateBookingFormV4, "bookingroom", "id">[];
    register: UseFormRegister<CreateBookingFormV4>;
    control: Control<CreateBookingFormV4>;
    errors: FieldErrors<CreateBookingFormV4>;
    setValue: UseFormSetValue<CreateBookingFormV4>;
    watch: UseFormWatch<CreateBookingFormV4>;
    setError: UseFormSetError<CreateBookingFormV4>;
    deleteRoom: (index: number) => void;
    roomSelected: RoomSelected[];
    setRoomSelected: React.Dispatch<React.SetStateAction<RoomSelected[]>>;
    clearErrors: UseFormClearErrors<CreateBookingFormV4>;
    processingStatus: Record<number, RoomStatus>;
    isPending: boolean;
    createdRooms: CreatedRoom[];
    bookingData?: ShowBookingDataV4;
    isGroupBookingComplete: boolean;
    hasDateChanged: boolean;
}

const InvoiceFormV4: React.FC<InvoiceFormPropsV4> = ({
    fields,
    register,
    control,
    errors,
    setValue,
    watch,
    setError,
    deleteRoom,
    roomSelected,
    setRoomSelected,
    clearErrors,
    processingStatus,
    isPending,
    createdRooms,
    bookingData,
    isGroupBookingComplete,
    hasDateChanged
}) => {
    const isUpdateMode = !!bookingData;
    return (
        <div className="d-flex flex-column" style={{ height: '100%' }}>
            <div className="action-sheet-content flex-grow-1">
                {/* Bagian Atas Form: Invoice Date, Status, Mark */}
                <div className="row mb-3 align-items-start">
                    {/* Kolom kiri bisa diisi dengan info tambahan jika perlu */}
                    <div className="col-sm-8">
                        {isUpdateMode && bookingData && (
                            <div className="d-flex flex-column gap-2">
                                <OtaOrLogo
                                    otaName={bookingData.booking[0]?.ota_name ?? ''}
                                    source={bookingData.source}
                                    logoUrl={bookingData.property?.logo_url ?? undefined}
                                    propertyName={bookingData.booking[0]?.agent?.name ?? bookingData.property?.name ?? ''}
                                />
                                <span className="fw-bold cursor-pointer">
                                    {bookingData.booking[0]?.voucher_number && (
                                        <a href={`${NAVIGATION_URLS.DASHBOARD_ADMIN_RESERVATION_INVOICE_VOUCHER}/${bookingData.booking[0]?.voucher_number}`} className="text-reset text-decoration-none" target="_blank">
                                            View Voucher
                                        </a>
                                    )}
                                    {bookingData.booking[0]?.voucher_number && (' - ')}
                                    <a href={`${NAVIGATION_URLS.DASHBOARD_ADMIN_RESERVATION_INVOICE_VIEW}/${bookingData.booking[0]?.invoice_number}`} className="text-warning text-decoration-none" target="_blank">View Invoice</a> -
                                    <a href={`${import.meta.env.VITE_API_URL}/v4/registration-form/${bookingData.booking[0]?.invoice_number}`} className="text-primary text-decoration-none" target="_blank">Reg Form</a>
                                </span>
                                <span className="fw-bold cursor-pointer">
                                    {/* <a href={`${NAVIGATION_URLS.DASHBOARD_ADMIN_RESERVATION_INVOICE_VIEW}/${bookingData.booking[0]?.invoice_number}?download=true`} className="text-reset text-decoration-none" target="_blank">Download PDF Invoice</a> - */}
                                    <a href={`${NAVIGATION_URLS.DASHBOARD_ADMIN_RESERVATION_INVOICE_VOUCHER}/${bookingData.booking[0]?.voucher_number}?download=true`} className="text-reset text-decoration-none" target="_blank">Download PDF Voucher</a> -
                                    <a href={`${import.meta.env.VITE_API_URL}/v4/invoice/${bookingData.booking[0]?.invoice_number}?download=1`} className="text-warning text-decoration-none" target="_blank">Download PDF Invoice</a> -
                                    <a href={`${NAVIGATION_URLS.DASHBOARD_ADMIN_RESERVATION_INVOICE_GUEST_DETAIL_VIEW}/${bookingData.booking[0]?.invoice_number}?download=true`} className="text-secondary text-decoration-none" target="_blank">Guest Details</a>
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Kolom Tengah: Invoice Date */}
                    <div className="col-sm-4">
                        <div className="col-12">
                            <label className="col-form-label" htmlFor="invoice_date">Invoice Date:</label>
                            <DateInput
                                isInvalid={!!errors.invoice_date}
                                style={{ fontSize: '14px', height: '35px' }}
                                {...register('invoice_date')}
                                defaultValue=""
                            />
                            {errors.invoice_date && <div className="invalid-feedback">{errors.invoice_date.message}</div>}
                        </div>
                    </div>
                </div>

                {/* Divider atau Spasi */}
                <div className="my-3"></div>

                {/* Loop untuk setiap kamar yang ditambahkan */}
                {fields.map((field, index) => {
                    const bookingId = createdRooms.find(cr => cr.index === index)?.bookingId ?? null;

                    return (
                        <RoomRatesComponentV4
                            key={field.id} // Gunakan field.id dari useFieldArray sebagai key
                            index={index}
                            register={register}
                            control={control}
                            errors={errors}
                            setValue={setValue}
                            watch={watch}
                            setError={setError}
                            deleteRoom={deleteRoom}
                            roomSelected={roomSelected}
                            setRoomSelected={setRoomSelected}
                            clearErrors={clearErrors}
                            statusInfo={processingStatus[index] || { status: 'idle' }}
                            isParentPending={isPending}
                            bookingId={bookingId}
                            isGroupBookingComplete={isGroupBookingComplete}
                            hasDateChanged={hasDateChanged}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default InvoiceFormV4;